import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import flexiicon from "../../../../assets/images/Flexi.svg";
import onlineFlexiIcon from "../../../../assets/images/OnlineFlexi.svg";
import goldIcon from "../../../../assets/images/Gold.svg";
import platiniumIcon from "../../../../assets/images/Platinium.svg";
import greendonecheck from "../../../../assets/images/greendone.svg";
import Loader from "../../../../components/common/Loader/Loader";
import Shimmer from "../../../../components/common/Loader/Shimmer";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { getProductCoversWithCodes } from "../../../../api/contentServices";

// Map plan code → plan icon. PL/OPL share Platinium.svg; TPL/TPPlus/ST and any
// other unknown code fall back to the Flexi icon. Human-readable legacy values
// (e.g. "Gold", "Platinum", "Flexi") are also handled so older payloads that
// store the display name in PlanName still resolve to the correct icon.
const PLAN_ICON_MAP = {
  FL: flexiicon,
  FLEXI: flexiicon,
  OFL: onlineFlexiIcon,
  "ONLINE FLEXI": onlineFlexiIcon,
  GO: goldIcon,
  GOLD: goldIcon,
  PL: platiniumIcon,
  OPL: platiniumIcon,
  PLATINUM: platiniumIcon,
  PLATINIUM: platiniumIcon,
};

// Map plan code → display name (EN / AR). Keys cover both short codes and
// common human-readable legacy values; unknown codes fall back to Flexi Plan.
const PLAN_NAME_MAP = {
  FL: { en: "Flexi Plan", ar: "الخطة المرنة" },
  FLEXI: { en: "Flexi Plan", ar: "الخطة المرنة" },
  OFL: { en: "Online Flexi Plan", ar: "الخطة المرنة أونلاين" },
  "ONLINE FLEXI": { en: "Online Flexi Plan", ar: "الخطة المرنة أونلاين" },
  GO: { en: "Gold Plan", ar: "الخطة الذهبية" },
  GOLD: { en: "Gold Plan", ar: "الخطة الذهبية" },
  PL: { en: "Platinum Plan", ar: "الخطة البلاتينية" },
  PLATINUM: { en: "Platinum Plan", ar: "الخطة البلاتينية" },
  PLATINIUM: { en: "Platinum Plan", ar: "الخطة البلاتينية" },
  OPL: { en: "Online Platinum Plan", ar: "الخطة البلاتينية أونلاين" },
  TPL: { en: "Third Party", ar: "طرف ثالث" },
  TPPLUS: { en: "Third Party Plus", ar: "الطرف الثالث +" },
  ST: { en: "Staff", ar: "الموظفين" },
  STAFF: { en: "Staff", ar: "الموظفين" },
  STANDARD: { en: "Standard Plan", ar: "الخطة القياسية" },
};

// Resolve plan icon + name from any combination of PlanCode/PlanName values.
// Tries an exact map lookup first on each candidate, then falls back to
// substring matching against well-known keywords (e.g. "Third Party Liability"
// → TPL). Returns Flexi defaults if nothing matches.
const resolvePlan = (...candidates) => {
  const normalized = candidates
    .map((c) => String(c || "").trim().toUpperCase())
    .filter(Boolean);

  for (const value of normalized) {
    if (PLAN_NAME_MAP[value]) {
      return {
        icon: PLAN_ICON_MAP[value] || flexiicon,
        name: PLAN_NAME_MAP[value],
      };
    }
  }

  const haystack = normalized.join(" ");
  const pick = (key) => ({
    icon: PLAN_ICON_MAP[key] || flexiicon,
    name: PLAN_NAME_MAP[key],
  });
  // Order matters: check longer/more-specific phrases before shorter ones.
  if (haystack.includes("TP+") || haystack.includes("THIRD PARTY PLUS"))
    return pick("TPPLUS");
  if (haystack.includes("TPL") || haystack.includes("THIRD PARTY"))
    return pick("TPL");
  if (haystack.includes("ONLINE FLEXI")) return pick("OFL");
  if (haystack.includes("ONLINE PLATIN")) return pick("OPL");
  if (haystack.includes("PLATIN")) return pick("PL");
  if (haystack.includes("GOLD")) return pick("GO");
  if (haystack.includes("FLEXI")) return pick("FL");
  if (haystack.includes("STANDARD")) return pick("ST");

  return { icon: flexiicon, name: { en: "Flexi Plan", ar: "الخطة المرنة" } };
};

const CoversAndBenefitTab = ({ policyDetails }) => {
  const translate = useLanguageTranslate();
  const { t } = useTranslation();

  const [apiBenefits, setApiBenefits] = useState([]);
  const [benefitsLoading, setBenefitsLoading] = useState(false);

  // Build a stable signature of policy cover codes (sorted, joined) so the
  // effect below only re-runs when the actual codes change — not on every
  // re-render where PolicyHeader.Covers is a new array reference.
  const policyCoversSignature = Array.isArray(
    policyDetails?.PolicyHeader?.Covers
  )
    ? policyDetails.PolicyHeader.Covers.map((c) =>
        String(c?.coverCode || c?.CoverCode || "").trim()
      )
        .filter(Boolean)
        .sort()
        .join(",")
    : "";

  // Fetch the Strapi covers for this product, then filter them down to the
  // cover codes that actually exist on this policy (per the policy/view
  // response — `policyDetails.PolicyHeader.Covers[].coverCode`).
  //
  // Strict intersection: a benefit is only shown when its cover code is
  // returned by BOTH policy/view AND the Strapi covers content. If either
  // side is missing/empty, no benefits render.
  useEffect(() => {
    const productCode = policyDetails?.PolicyHeader?.ProductCode;
    if (!productCode) return;

    let cancelled = false;
    setApiBenefits([]);
    setBenefitsLoading(true);
    getProductCoversWithCodes(productCode)
      .then((result) => {
        if (cancelled) return;
        if (!result.success || result.data.length === 0) return;

        const policyCoverCodes = policyCoversSignature
          ? policyCoversSignature.split(",")
          : [];
        if (policyCoverCodes.length === 0) return;

        const matchingCovers = result.data.filter((cover) =>
          policyCoverCodes.includes(String(cover.coverCode).trim())
        );

        const benefits = matchingCovers.flatMap(
          (cover) => cover.descriptions || []
        );
        setApiBenefits(benefits);
      })
      .finally(() => {
        if (!cancelled) setBenefitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [policyDetails?.PolicyHeader?.ProductCode, policyCoversSignature]);


  // Plan icon + title resolve from PlanCode and PlanName together. PlanCode is
  // the canonical short code (e.g. "TPL"); PlanName may carry either a code or
  // a human-readable label (e.g. "Third Party Liability"). resolvePlan tries
  // exact lookup first, then substring matching for legacy/long-form values.
  const { icon: planIcon, name: planName } = resolvePlan(
    policyDetails?.PolicyHeader?.PlanCode,
    policyDetails?.PolicyHeader?.PlanName
  );

  return (
    <div className="bg-white rounded-lg p-6 w-5/6 border bg-white rounded-[10px] border-solid border-[#DDE2E4] ipad:w-full max-600:w-full max767:border-0 max767:p-5">
      {/* Plan Title — icon swaps per plan code */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <div className="">
            <img src={planIcon} className="mr-2" alt="Plan icon" />
          </div>
          <h2 className="text-[#48535B] text-[32px] not-italic font-semibold leading-[120%] max767:text-[24px]">
            {translate(planName.en, planName.ar)}
          </h2>
        </div>
      </div>

      <div className="flex gap-4 max-600:block">
        <div className="includeSection w-3/5 max-600:w-full ipad:w-[50%]">
          <h3 className="text-[#48535B] text-lg not-italic font-medium leading-[normal] mb-4">
            {t("motorInsurance.covergeAndBenefit.benefit")}
          </h3>
          {benefitsLoading ? (
            <div
              className="space-y-3 mb-6"
              role="status"
              aria-label={translate("Loading benefits...", "جاري تحميل المزايا...")}
            >
              {[85, 70, 90, 60, 78].map((w, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Shimmer className="flex-shrink-0 w-4 h-4 mt-1 rounded-full" />
                  <Shimmer className="h-3.5" style={{ width: `${w}%` }} />
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-2 mb-6">
              {apiBenefits.map((benefit, index) => (
                <li
                  key={index}
                  className="flex items-center text-black text-sm not-italic font-normal leading-[150%]"
                >
                  <img
                    src={greendonecheck}
                    className="mr-2"
                    alt="green check mark"
                  />
                  {typeof benefit === "string"
                    ? benefit
                    : translate(benefit.en, benefit.ar)}
                </li>
              ))}
            </ul>
          )}
        </div>

      </div>

    </div>
  );
};

export default CoversAndBenefitTab;
